import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { GlobalModals, GenericModals, OrderTripTypes, JourneyDirections } from '../../constants/AppConstants';
import DisruptionAlertActions from '../../actions/DisruptionAlertActions';
import ModalActions from '../../actions/ModalActions';
import TrackingActions from '../../actions/TrackingActions';
import useFeatureFlag from '../../hooks/useFeatureFlag';
import UserStore from '../../stores/UserStore';
import DisruptionAlertStore from '../../stores/DisruptionAlertStore';
import { getMergedSplitJourneys } from '../../utils/splits';
import Order from '../../records/Order';
import OrderTrip from '../../records/order/Trip';
import User from '../../records/User';

import NotificationPreferences from '../../components/account/NotificationPreferences';
import DisruptionAlertService from '../../services/DisruptionAlertService';

import AlertButton from './AlertButton';
import JourneySelectorModal from './modals/JourneySelectorModal';
import AlertBuilderModal from './modals/AlertBuilderModal';
import SuccessModal from './modals/SuccessModal';
import { notificationPreferences } from '../../constants/AppConstants';
import { DisruptionAlertsLimitExceededError } from '../../errors/DisruptionAlerts';
import connectToStores from '../../helpers/connectToStores';


function getStateFromStores() {
    const user = UserStore.getCurrent();
    return { user };
}

const CreateAlert = ({
    buttonProps,
    context = '',
    offerReturnJourneyAlert = false,
    order,
    orderTrip,
    additionalOrderTrips = new List(),
    prefillFromSearch,
    user,
}) => {
    const enableDisruptionAlerts = useFeatureFlag('enableDisruptionAlerts');
    const enableDisruptionAlertsCreateButton = useFeatureFlag('enableDisruptionAlertsCreateButton');

    const [alertBuilderJourneyDirection, setAlertBuilderJourneyDirection] = useState(JourneyDirections.OUTWARD);
    const [showCreateReturnAlertOption, setShowCreateReturnAlertOption] = useState(offerReturnJourneyAlert);
    const [alertCreationModalVisible, setAlertCreationModalVisible] = useState(false);
    const [journeySelectorModalVisible, setJourneySelectorModalVisible] = useState(false);
    const [successModalVisible, setShowSuccessModalVisible] = useState(false);
    const [showNotificationPrefsModal, setShowNotificationPrefsModal] = useState(false);
    const [isOneOffAlert, setIsOneOffAlert] = useState(false);
    const [outwardAlert, setOutwardAlert] = useState(null);

    const showAlertCreationModal = () => setAlertCreationModalVisible(true);
    const hideAlertCreationModal = () => setAlertCreationModalVisible(false);

    const showJourneySelectorModal = () => setJourneySelectorModalVisible(true);
    const hideJourneySelectorModal = () => setJourneySelectorModalVisible(false);

    const showSuccessModal = () => setShowSuccessModalVisible(true);
    const hideSuccessModal = () => setShowSuccessModalVisible(false);

    if (!enableDisruptionAlerts || !enableDisruptionAlertsCreateButton) {
        return null;
    }

    const groupTrips = orderTrip ? [orderTrip, ...Array.from(additionalOrderTrips || [])] : null;
    const isSplitGroup = !!(groupTrips && groupTrips.some(trip => trip?.isSplitTrip?.()));

    // one merged whole-journey record per direction — one alert per direction
    const splitJourneys = (() => {
        if (!isSplitGroup) {
            return null;
        }
        const { outwardJourney, returnJourney } = getMergedSplitJourneys(groupTrips);
        return new List([outwardJourney, returnJourney].filter(Boolean));
    })();

    const onOpenModal = () => TrackingActions.genericModalOpen(GenericModals.DISRUPTION_ALERT);

    const onCreateAlert = async () => {
        // Authentication check before creating any alerts
        if (!user || !user.isLoggedIn()) {
            return ModalActions.open(GlobalModals.LOGIN, {
                onLogin: launchAlertCreationModal,
                onRegisterSuccessClose: launchAlertCreationModal,
            });
        }
        const preferences = await DisruptionAlertService.getPreferences(user.id);
        const { isEmailNotification, isCloudMessageNotification } = preferences;

        if (!isEmailNotification && !isCloudMessageNotification) {
            return setShowNotificationPrefsModal(true);
        }

        return launchAlertCreationModal();
    };

    const launchAlertCreationModal = () => {
        // Decide which alert creation modal to show
        const journeySelectorTripTypes = new List([
            OrderTripTypes.SINGLE,
            OrderTripTypes.RETURN,
            OrderTripTypes.RETURN_SINGLE,
            OrderTripTypes.RETURN_SINGLES,
        ]);

        if (order && orderTrip && isSplitGroup) {
            return showJourneySelectorModal();
        }

        if (order && orderTrip && journeySelectorTripTypes.some(type => orderTrip.getType() === type)) {
            return showJourneySelectorModal();
        }

        return showAlertCreationModal();
    };

    const onJourneySelectorSubmit = async orderJourneys => {
        hideJourneySelectorModal();

        let alertCreationError = false;
        for (const orderJourney of orderJourneys) {
            try {
                await DisruptionAlertActions.createFromOrderJourney({ user, order, orderTrip, orderJourney, context });
            } catch (err) {
                alertCreationError = true;
                if (err instanceof DisruptionAlertsLimitExceededError) {
                    return ModalActions.open(GlobalModals.ERROR, {
                        title: 'disruptionAlerts.modals.limitExceeded.title',
                        message: 'disruptionAlerts.modals.limitExceeded.message'
                    });
                } else {
                    throw err;
                }
            }
        }

        if (!alertCreationError) {
            setIsOneOffAlert(true);
            showSuccessModal();
        }

        return null;
    };

    const onAlertBuilderSubmit = async ({ alrt, success, err }) => {
        if (success) {
            if (outwardAlert) {
                setOutwardAlert(null);
            } else {
                setOutwardAlert(showCreateReturnAlertOption ? alrt : null);
            }
            showSuccessModal();
            hideAlertCreationModal();
        } else if (err instanceof DisruptionAlertsLimitExceededError) {
            ModalActions.open(GlobalModals.ERROR, {
                title: 'disruptionAlerts.modals.limitExceeded.title',
                message: 'disruptionAlerts.modals.limitExceeded.message'
            });
        }
        setAlertBuilderJourneyDirection(JourneyDirections.OUTWARD);
    };

    const onCreateReturnAlert = () => {
        setShowCreateReturnAlertOption(false);
        setAlertBuilderJourneyDirection(JourneyDirections.INWARD);
        showAlertCreationModal();
    };

    const onHideSuccessModal = () => {
        setShowCreateReturnAlertOption(offerReturnJourneyAlert);
        hideSuccessModal();
    };

    const matchingAlert = order && orderTrip && (isSplitGroup
        ? groupTrips.some(trip => DisruptionAlertStore.hasMatchingAlert({ order, orderTrip: trip }))
        : DisruptionAlertStore.hasMatchingAlert({ order, orderTrip }));
    const alertButtonProps = {
        ...buttonProps,
        ...(matchingAlert ? { hasMatchingAlert: true } : { onClick: onCreateAlert }),
    };

    const sharedModalProps = {
        disableBlur: true,
        headerClass: 'text-left',
        onEnterContinue: false,
        onOpen: onOpenModal,
        show: true,
        titleText: 'disruptionAlerts.modals.common.title',
    };

    return (
        <Fragment>
            <AlertButton {...alertButtonProps} />
            {showNotificationPrefsModal && <NotificationPreferences
                user={user}
                isDDM
                notificationPreferencesModalOpen
                hideSummary
                defaultNotificationPreference={notificationPreferences.EMAIL}
                disableCloseWithoutChoosingPreference
                onContinueAction={launchAlertCreationModal}
            />}
            {journeySelectorModalVisible && (
                <JourneySelectorModal
                    {...sharedModalProps}
                    onClose={hideJourneySelectorModal}
                    onConfirm={onJourneySelectorSubmit}
                    order={order}
                    orderTrip={orderTrip}
                    journeys={splitJourneys || undefined}
                />
            )}
            {alertCreationModalVisible && (
                <AlertBuilderModal
                    {...sharedModalProps}
                    journeyDirection={alertBuilderJourneyDirection}
                    onAlertBuilderSubmit={onAlertBuilderSubmit}
                    order={order}
                    orderTrip={orderTrip}
                    outwardAlert={outwardAlert}
                    prefillFromSearch={prefillFromSearch}
                    user={user}
                    hideAlertCreationModal={hideAlertCreationModal}
                />
            )}
            {successModalVisible && (
                <SuccessModal
                    {...sharedModalProps}
                    isOneOffAlert={isOneOffAlert}
                    onClose={onHideSuccessModal}
                    onCreateReturnAlert={onCreateReturnAlert}
                    showCreateReturnAlertOption={showCreateReturnAlertOption}
                />
            )}
        </Fragment>
    );
};

CreateAlert.propTypes = {
    buttonProps: PropTypes.object,
    context: PropTypes.string,
    order: PropTypes.instanceOf(Order),
    orderTrip: PropTypes.instanceOf(OrderTrip),
    additionalOrderTrips: PropTypes.instanceOf(List),
    prefillFromSearch: PropTypes.bool,
    offerReturnJourneyAlert: PropTypes.bool,
    user: PropTypes.instanceOf(User).isRequired,
};

export default connectToStores([UserStore], getStateFromStores)(CreateAlert);

